import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Radio, Megaphone, Loader2, CheckCircle2, Facebook, Instagram,
  Twitter, Youtube, ShieldCheck, Sparkles, Clock
} from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUBLIC = axios.create({ baseURL: `${BACKEND}/api` });

const fmtMoney = (amount, currency) => {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "USD").toUpperCase() }).format(amount);
  } catch {
    return `${currency?.toUpperCase() || "USD"} ${amount?.toFixed(2)}`;
  }
};

export default function SponsorBriefPublic() {
  const { stationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    sponsor_name: "",
    sponsor_email: "",
    sponsor_phone: "",
    advertiser: "",
    product: "",
    benefits: "",
    audience: "",
    cta: "",
    duration_sec: 30,
    tone: "energetic",
    language: "English",
    extra_context: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: d } = await PUBLIC.get(`/public/sponsor/${stationId}`);
        if (mounted) setData(d);
      } catch (e) {
        if (mounted) setData({ unavailable: true });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [stationId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const prices = data?.pricing?.prices || {};
  const currency = data?.pricing?.currency || "usd";
  const selectedPrice = Number(prices[String(form.duration_sec)] || 0);

  const submit = async () => {
    if (!form.advertiser.trim() || !form.sponsor_email.trim()) {
      toast.error("Advertiser and email are required");
      return;
    }
    if (selectedPrice <= 0) {
      toast.error("This spot length is not priced yet — try a different duration");
      return;
    }
    setSubmitting(true);
    try {
      const { data: r } = await PUBLIC.post(`/public/sponsor/${stationId}/checkout`, {
        ...form,
        origin_url: window.location.origin,
      });
      if (r.url) {
        window.location.href = r.url;
      } else {
        toast.error("Checkout could not be started");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-ink-muted">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading station…
      </div>
    );
  }

  if (data?.unavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg p-6">
        <div className="max-w-md text-center">
          <Megaphone className="h-10 w-10 text-brand-amber mx-auto mb-3" />
          <h1 className="font-display text-3xl font-black tracking-tighter mb-2">Sponsorships unavailable</h1>
          <p className="text-sm text-ink-muted">This station isn&apos;t accepting public sponsorship briefs right now.</p>
        </div>
      </div>
    );
  }

  const st = data.station;
  const socials = [
    { icon: Facebook, url: st.facebook },
    { icon: Instagram, url: st.instagram },
    { icon: Twitter, url: st.twitter },
    { icon: Youtube, url: st.youtube },
  ].filter((s) => s.url);

  return (
    <div className="min-h-screen bg-bg text-ink-primary" data-testid="sponsor-public-page">
      {/* Top band */}
      <header className="border-b border-line bg-bg-surface">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {st.logo_url ? (
              <img src={st.logo_url} alt="" className="h-10 w-10 rounded-md object-cover border border-line" />
            ) : (
              <div className="h-10 w-10 rounded-md bg-brand-amber/15 flex items-center justify-center">
                <Radio className="h-5 w-5 text-brand-amber" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display font-bold text-ink-primary truncate">{st.name || "Station"}</div>
              <div className="text-xs text-ink-muted truncate">{st.tagline || st.genre || ""}</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-ink-muted">
            {socials.map(({ icon: Icon, url }, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer" className="hover:text-cyan-300 transition">
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-line bg-bg-surface/40">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80 mb-3">// SPONSOR THIS STATION</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-ink-primary leading-[1.02]">
            Run your ad on <span className="text-brand-amber">{st.name}</span>.
          </h1>
          {data.blurb && (
            <p className="mt-4 max-w-2xl text-base text-ink-secondary">{data.blurb}</p>
          )}

          {/* Pricing chips */}
          <div className="mt-6 flex flex-wrap gap-2">
            {[15, 30, 60].map((d) => {
              const p = Number(prices[String(d)] || 0);
              if (p <= 0) return null;
              const active = form.duration_sec === d;
              return (
                <button
                  key={d}
                  onClick={() => update("duration_sec", d)}
                  data-testid={`sponsor-price-${d}`}
                  className={`rounded-full border px-4 py-2 text-sm font-mono transition ${
                    active
                      ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                      : "border-line bg-bg-elevated text-ink-secondary hover:text-ink-primary"
                  }`}
                >
                  {d}s · {fmtMoney(p, currency)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <main className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        <form
          className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-6 space-y-4"
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          data-testid="sponsor-form"
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <PField label="Your name *" value={form.sponsor_name} onChange={(v) => update("sponsor_name", v)} testid="sp-name" />
            <PField label="Email *" type="email" value={form.sponsor_email} onChange={(v) => update("sponsor_email", v)} testid="sp-email" />
            <PField label="Phone (optional)" value={form.sponsor_phone} onChange={(v) => update("sponsor_phone", v)} testid="sp-phone" />
            <PField label="Business / Brand *" value={form.advertiser} onChange={(v) => update("advertiser", v)} testid="sp-advertiser" />
          </div>

          <PField label="Product or service" value={form.product} onChange={(v) => update("product", v)} testid="sp-product" />
          <PTextarea label="Key benefits" rows={2} value={form.benefits} onChange={(v) => update("benefits", v)} placeholder="Why are you better than the competition?" testid="sp-benefits" />
          <PField label="Target audience" value={form.audience} onChange={(v) => update("audience", v)} testid="sp-audience" placeholder="e.g. busy parents, young commuters" />
          <PField label="Call to action" value={form.cta} onChange={(v) => update("cta", v)} testid="sp-cta" placeholder="e.g. Visit acmepizza.com or call 555-1234" />

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Duration</Label>
              <Select value={String(form.duration_sec)} onValueChange={(v) => update("duration_sec", Number(v))}>
                <SelectTrigger className="mt-1 bg-bg border-line" data-testid="sp-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[15, 30, 60].filter((d) => Number(prices[String(d)] || 0) > 0).map((d) => (
                    <SelectItem key={d} value={String(d)}>{d}s — {fmtMoney(prices[String(d)] || 0, currency)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Tone</Label>
              <Select value={form.tone} onValueChange={(v) => update("tone", v)}>
                <SelectTrigger className="mt-1 bg-bg border-line" data-testid="sp-tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(data.tones || ["energetic"]).map((t) => (
                    <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <PTextarea label="Extra context (optional)" rows={2} value={form.extra_context} onChange={(v) => update("extra_context", v)} placeholder="Promo dates, location restrictions, mandatory legal lines…" testid="sp-extra" />

          {data.terms && (
            <div className="rounded-md border border-line bg-bg-elevated p-3 text-xs text-ink-muted">
              <ShieldCheck className="h-3.5 w-3.5 text-brand-amber inline mr-1.5" />
              {data.terms}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || selectedPrice <= 0}
            data-testid="sponsor-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-6 py-3 font-semibold text-black amber-glow disabled:opacity-50 transition"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Submit &amp; pay {selectedPrice > 0 ? fmtMoney(selectedPrice, currency) : ""}
          </button>
          <p className="text-[11px] text-ink-muted text-center">
            Secure checkout via Stripe. Refundable if your spot can&apos;t be produced.
          </p>
        </form>

        {/* Side info */}
        <aside className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80 mb-2">// HOW IT WORKS</div>
            <ol className="space-y-3 text-sm text-ink-secondary">
              <Step n="1" title="Tell us about your business" body="A short brief — 60 seconds tops." />
              <Step n="2" title="Pay securely" body="Card via Stripe. No subscription, one-time." />
              <Step n="3" title="We produce your spot" body="AI scripts, pro voiceover, mixed with music — air-ready in days." />
              <Step n="4" title="It runs on the station" body="Approved and scheduled by the station team." />
            </ol>
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-5">
            <div className="flex items-center gap-2 mb-2 text-brand-amber text-sm">
              <CheckCircle2 className="h-4 w-4" />
              <span className="font-semibold">Money-back guarantee</span>
            </div>
            <p className="text-xs text-ink-muted">If we can&apos;t produce your spot or the station declines it, you&apos;ll get a full refund.</p>
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-5">
            <div className="flex items-center gap-2 mb-2 text-ink-secondary text-sm">
              <Clock className="h-4 w-4 text-brand-amber" />
              <span>Typical delivery: <b className="text-ink-primary">2–5 days</b></span>
            </div>
          </div>
        </aside>
      </main>

      <footer className="border-t border-line">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-ink-muted flex items-center justify-between">
          <span>© {new Date().getFullYear()} {st.name}</span>
          <span className="font-mono">Powered by BroadcastFlow AI Content Hub</span>
        </div>
      </footer>
    </div>
  );
}

function PField({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink-secondary">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        className="mt-1 bg-bg border-line"
        data-testid={testid}
      />
    </div>
  );
}

function PTextarea({ label, value, onChange, testid, rows = 3, placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink-secondary">{label}</Label>
      <Textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        className="mt-1 bg-bg border-line"
        data-testid={testid}
      />
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 h-6 w-6 rounded-full bg-brand-amber/15 text-brand-amber font-mono text-[11px] flex items-center justify-center mt-0.5">{n}</span>
      <div>
        <div className="text-ink-primary text-sm font-semibold">{title}</div>
        <div className="text-xs text-ink-muted">{body}</div>
      </div>
    </li>
  );
}
